import type {
  AIProvider,
  DesignBrief,
  DesignDirective,
  ImageAnalysis,
} from '@/types';
import { clamp } from '@/lib/utils';
import { heuristicDirective } from './heuristicProvider';

/**
 * Claude vision provider (browser side).
 *
 * The browser never talks to the Anthropic API directly and never holds the
 * key — it POSTs to this app's own proxy (`server/proxy.mjs`), which makes the
 * Messages API call with the official SDK. See README "Connecting a real AI".
 *
 * Every failure path degrades to the local heuristic, so a missing key, a
 * network error, or a malformed response costs the user nothing but the
 * semantic layer.
 */

const PROXY_URL = import.meta.env.VITE_AI_PROXY_URL || '/api/analyze';
const ENABLED = import.meta.env.VITE_AI_ENABLED !== 'false';

export class ClaudeVisionProvider implements AIProvider {
  id = 'claude';
  label = 'Claude vision';

  private reachable: boolean | null = null;

  available(): boolean {
    return ENABLED && this.reachable !== false;
  }

  async probe(): Promise<boolean> {
    if (!ENABLED) return false;
    try {
      const res = await fetch(PROXY_URL.replace(/\/analyze$/, '/health'), {
        method: 'GET',
      });
      const body = (await res.json()) as { configured?: boolean };
      this.reachable = res.ok && body.configured === true;
    } catch {
      this.reachable = false;
    }
    return this.reachable;
  }

  async analyze(
    imageDataUrl: string,
    analysis: ImageAnalysis,
    brief: DesignBrief,
  ): Promise<DesignDirective> {
    const fallback = heuristicDirective(analysis, brief);
    if (!ENABLED) return fallback;

    try {
      const res = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          image: imageDataUrl,
          brief,
          // The local measurements go along as hints. The model reasons about
          // what the photo *is*; it does not have to re-derive luminance.
          analysis: compactAnalysis(analysis),
        }),
      });

      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`Proxy responded ${res.status}: ${detail.slice(0, 200)}`);
      }

      const raw = (await res.json()) as Partial<DesignDirective>;
      this.reachable = true;
      return mergeDirective(fallback, raw);
    } catch (err) {
      this.reachable = false;
      const message = err instanceof Error ? err.message : String(err);
      return {
        ...fallback,
        notes: [...fallback.notes, `Vision model unavailable (${message}). Used the built-in engine.`],
      };
    }
  }
}

/** Trim the analysis to what a model can actually use, to keep the prompt small. */
function compactAnalysis(a: ImageAnalysis) {
  return {
    aspect: Number(a.aspect.toFixed(3)),
    averageLuminance: Number(a.averageLuminance.toFixed(3)),
    contrastSpread: Number(a.contrastSpread.toFixed(3)),
    busy: a.busy,
    focalPoint: {
      x: Number(a.focalPoint.x.toFixed(3)),
      y: Number(a.focalPoint.y.toFixed(3)),
    },
    subject: {
      x: Number(a.subject.x.toFixed(3)),
      y: Number(a.subject.y.toFixed(3)),
      w: Number(a.subject.w.toFixed(3)),
      h: Number(a.subject.h.toFixed(3)),
    },
    faces: a.faces.map((f) => ({
      x: Number(f.x.toFixed(3)),
      y: Number(f.y.toFixed(3)),
      w: Number(f.w.toFixed(3)),
      h: Number(f.h.toFixed(3)),
      source: f.source,
    })),
    dominantColors: a.dominantColors.map((c) => ({
      hex: c.hex,
      weight: Number(c.weight.toFixed(3)),
    })),
    zones: a.zones.slice(0, 6).map((z) => ({
      id: z.id,
      luminance: Number(z.luminance.toFixed(3)),
      detail: Number(z.detail.toFixed(3)),
      score: Number(z.score.toFixed(3)),
      faceOverlap: Number(z.faceOverlap.toFixed(3)),
    })),
  };
}

const VALID_RECIPES = new Set([
  'lower-third',
  'editorial-stack',
  'centered-hero',
  'side-panel',
  'top-kicker',
  'framed-minimal',
  'split-band',
  'corner-anchor',
]);
const VALID_ZONES = new Set([
  'bottom', 'top', 'left', 'right', 'center',
  'top-left', 'top-right', 'bottom-left', 'bottom-right',
]);
const VALID_OVERLAYS = new Set(['none', 'scrim', 'panel', 'blur-panel', 'full-wash']);
const VALID_CORNERS = new Set(['top-left', 'top-right', 'bottom-left', 'bottom-right']);
const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Take only the fields the model got right. Structured outputs already
 * guarantee the shape, but the values still need range checks — and a partial
 * response should degrade field-by-field, not wholesale.
 */
function mergeDirective(
  fallback: DesignDirective,
  raw: Partial<DesignDirective>,
): DesignDirective {
  const recipes = Array.isArray(raw.recipes)
    ? raw.recipes.filter((r) => VALID_RECIPES.has(r))
    : [];

  return {
    recipes: recipes.length ? recipes : fallback.recipes,
    textZone:
      typeof raw.textZone === 'string' && VALID_ZONES.has(raw.textZone)
        ? raw.textZone
        : fallback.textZone,
    textColorScheme:
      raw.textColorScheme === 'light' || raw.textColorScheme === 'dark'
        ? raw.textColorScheme
        : fallback.textColorScheme,
    overlayTreatment:
      typeof raw.overlayTreatment === 'string' && VALID_OVERLAYS.has(raw.overlayTreatment)
        ? raw.overlayTreatment
        : fallback.overlayTreatment,
    overlayStrength:
      typeof raw.overlayStrength === 'number' && Number.isFinite(raw.overlayStrength)
        ? clamp(raw.overlayStrength, 0, 0.95)
        : fallback.overlayStrength,
    accentColor:
      typeof raw.accentColor === 'string' && HEX.test(raw.accentColor)
        ? raw.accentColor
        : fallback.accentColor,
    emphasisWords: Array.isArray(raw.emphasisWords)
      ? raw.emphasisWords.filter((w) => typeof w === 'string').slice(0, 3)
      : fallback.emphasisWords,
    headlineWeight:
      typeof raw.headlineWeight === 'number' && raw.headlineWeight >= 100 && raw.headlineWeight <= 900
        ? raw.headlineWeight
        : fallback.headlineWeight,
    headlineCase:
      raw.headlineCase === 'upper' || raw.headlineCase === 'lower' || raw.headlineCase === 'none'
        ? raw.headlineCase
        : fallback.headlineCase,
    tracking:
      typeof raw.tracking === 'number' && Number.isFinite(raw.tracking)
        ? clamp(raw.tracking, -0.06, 0.5)
        : fallback.tracking,
    logoCorner:
      typeof raw.logoCorner === 'string' && VALID_CORNERS.has(raw.logoCorner)
        ? raw.logoCorner
        : fallback.logoCorner,
    notes: Array.isArray(raw.notes)
      ? raw.notes.filter((n) => typeof n === 'string').slice(0, 6)
      : fallback.notes,
  };
}
